import { createRootRoute, createRoute, createRouter, lazyRouteComponent, Link, type RouterHistory } from "@tanstack/react-router";
import type { JSX } from "react";
import { AgentsPage } from "../agents/AgentsPage";
import { AnalyticsPage } from "../analytics/AnalyticsPage";
import { AssistPage } from "../assist/AssistPage";
import { CallsPage } from "../calls/CallsPage";
import { ChatPage } from "../chat/ChatPage";
import { HandoffsPage } from "../calls/HandoffsPage";
import { CampaignsPage } from "../campaigns/CampaignsPage";
import { CompliancePage } from "../compliance/CompliancePage";
import { DevelopersPage } from "../developers/DevelopersPage";
import { HomePage } from "../home/HomePage";
import { KnowledgePage } from "../knowledge/KnowledgePage";
import { ClipsPage } from "../clips/ClipsPage";
import { ModelsPage } from "../models/ModelsPage";
import { useT } from "../i18n";
import { NumbersPage } from "../numbers/NumbersPage";
import { OperatorsPage } from "../operators/OperatorsPage";
import { ScriptsPage } from "../scripts/ScriptsPage";
import { SettingsPage } from "../settings/SettingsPage";
import { SipUsersPage } from "../sipusers/SipUsersPage";
import { TrunksPage } from "../trunks/TrunksPage";
import { Empty, Soon } from "../ui/states";
import { Page } from "./Page";
import { SECTIONS } from "./sections";
import { Shell } from "./Shell";

import { AGENT_TABS, type AgentTab } from "../agents/tabs";

const PAGES: Record<string, () => JSX.Element> = {
  "/": HomePage,
  "/chat": ChatPage,
  "/agents": AgentsPage,
  "/scripts": ScriptsPage,
  "/knowledge": KnowledgePage,
  "/clips": ClipsPage,
  "/models": ModelsPage,
  "/calls": CallsPage,
  "/campaigns": CampaignsPage,
  "/numbers": NumbersPage,
  "/trunks": TrunksPage,
  "/sip-users": SipUsersPage,
  "/operators": OperatorsPage,
  "/handoffs": HandoffsPage,
  "/assist": AssistPage,
  "/compliance": CompliancePage,
  "/analytics": AnalyticsPage,
  "/developers": DevelopersPage,
  "/settings": SettingsPage,
};

function NotFound() {
  const t = useT();
  return (
    <Empty title={t("state.notFound.title")}>
      <Link to="/" className="underline text-[var(--color-primary)]">
        {t("state.notFound.home")}
      </Link>
    </Empty>
  );
}

export function createAppRouter(history?: RouterHistory) {
  const root = createRootRoute({ component: () => <Shell />, notFoundComponent: NotFound });
  const routes = SECTIONS.map((section) => {
    const Built = PAGES[section.path];
    const component = Built ?? function Planned() {
      const t = useT();
      return (
        <Page title={t(section.label)}>
          <Soon release={section.release ?? ""} />
        </Page>
      );
    };
    return createRoute({ getParentRoute: () => root, path: section.path, component });
  });
  // the builder (the YAML library, CodeMirror later) loads when an agent is opened
  const agent = createRoute({
    getParentRoute: () => root,
    path: "/agents/$agentId",
    // ?tab=copilot opens the agent at a tab (the call inspector's "see the proposal"); ?created=1 just after the
    // new-agent dialog made it (V1.2-UX-T03)
    validateSearch: (search: Record<string, unknown>): { tab?: AgentTab; created?: boolean } => ({
      ...(AGENT_TABS.includes(search.tab as AgentTab) ? { tab: search.tab as AgentTab } : {}),
      ...(search.created === true || search.created === 1 || search.created === "1" ? { created: true } : {}),
    }),
    component: lazyRouteComponent(() => import("../agents/AgentPage"), "AgentPage"),
  });
  const call = createRoute({
    getParentRoute: () => root,
    path: "/calls/$callId",
    component: lazyRouteComponent(() => import("../calls/CallPage"), "CallPage"),
  });
  const newCampaign = createRoute({
    getParentRoute: () => root,
    path: "/campaigns/new",
    component: lazyRouteComponent(() => import("../campaigns/NewCampaignPage"), "NewCampaignPage"),
  });
  const campaign = createRoute({
    getParentRoute: () => root,
    path: "/campaigns/$campaignId",
    component: lazyRouteComponent(() => import("../campaigns/CampaignPage"), "CampaignPage"),
  });
  const collection = createRoute({
    getParentRoute: () => root,
    path: "/knowledge/$collectionId",
    component: lazyRouteComponent(() => import("../knowledge/CollectionPage"), "CollectionPage"),
  });
  const scriptApp = createRoute({
    getParentRoute: () => root,
    path: "/scripts/$appId",
    component: lazyRouteComponent(() => import("../scripts/ScriptAppPage"), "ScriptAppPage"),
  });
  return createRouter({
    routeTree: root.addChildren([...routes, agent, call, newCampaign, campaign, collection, scriptApp]), history, defaultPreload: "intent",
  });
}

declare module "@tanstack/react-router" {
  interface Register {
    router: ReturnType<typeof createAppRouter>;
  }
}
